/**
 * useOrbDocReferenceBehaviorsARDocBodyLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocReferenceBehaviorsARDocBody
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocReferenceBehaviorsARDocView as OrbDocReferenceBehaviorsARDocViewEntity, OrbDocReferenceBehaviorsARDocBodyEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocReferenceBehaviorsARDocBodyState =
  | 'idle';

export type OrbDocReferenceBehaviorsARDocBodyEvent =
  | 'INIT';

type OrbDocReferenceBehaviorsARDocBodySlot = 'main';
interface OrbDocReferenceBehaviorsARDocBodyInternalState {
  machineState: OrbDocReferenceBehaviorsARDocBodyState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocReferenceBehaviorsARDocBodyEvent | null;
  slotWriter: Partial<Record<OrbDocReferenceBehaviorsARDocBodySlot, OrbDocReferenceBehaviorsARDocBodyEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocReferenceBehaviorsARDocBodyEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocReferenceBehaviorsARDocViewEntity>;
}

type OrbDocReferenceBehaviorsARDocBodyAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocReferenceBehaviorsARDocBodyEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocReferenceBehaviorsARDocBodySlot[]; renderClear?: OrbDocReferenceBehaviorsARDocBodySlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocReferenceBehaviorsARDocBodyEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocReferenceBehaviorsARDocViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocReferenceBehaviorsARDocBodyReducer(
  state: OrbDocReferenceBehaviorsARDocBodyInternalState,
  action: OrbDocReferenceBehaviorsARDocBodyAction
): OrbDocReferenceBehaviorsARDocBodyInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocReferenceBehaviorsARDocBodyState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocReferenceBehaviorsARDocViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocReferenceBehaviorsARDocViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocReferenceBehaviorsARDocViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocReferenceBehaviorsARDocBodyInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { DocNavItem } from '@app/shared';
export interface OrbDocReferenceBehaviorsARDocBodyConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  content: { children: Array<{ children: Array<{ className: string; content: string; type: string; variant: string; weight: string } | { content: string; direction: string; type: string }>; direction: string; gap: string; id: string; type: string } | { content: string; direction: string; type: string }>; direction: string; gap: string; type: string };
  crumbs: Array<{ href: string; label: string } | { label: string }>;
  direction: 'ltr' | 'rtl';
  lede: string;
  navItems: Array<{ items: Array<{ active: boolean; href: string; label: string } | { items: { active: boolean; href: string; label: string }[]; label: string }>; label: string } | { items: { active: boolean; href: string; label: string }[]; label: string }>;
  next: { href: string; label: string };
  prev: { href: string; label: string };
  sidebarLabel: string;
  title: string;
  toc: { id: string; label: string; level: number }[];
  tocLabel: string;
}

const DEFAULT_ORB_DOC_REFERENCE_BEHAVIORS_AR_DOC_BODY_CONFIG = {
  content: { children: [{ content: "السلوك القياسي مداريّ تستورده بـ `uses Alias from \"std/behaviors/<name>\"` وتركّبه سمةً سمة (`trait Local = Alias.traits.Trait -> Entity { … }`) أو كاملًا (`orbital Local = Alias.orbitals.Orbital { … }`). يذكر كل مدخل ما يحق لموضع الاستدعاء أن يغيّره: السمات وأحداثها (تُعاد تسميتها بـ `events {}`) وما تبثّه ومفاتيح إعدادها (تُضبط بـ `config {}`). الحالات والانتقالات ملك للسلوك ولا تتغير عند الاستدعاء. مولَّد من سجل @almadar/std، والأوصاف بالإنجليزية كما في السجل.", direction: "rtl", type: "markdown-content" }, { children: [{ className: "scroll-mt-20", content: "السلوكيات", type: "typography", variant: "h2", weight: "semibold" }, { content: "| السلوكيات | سلوكًا |\n|---|---|\n| [نواة الواجهة](/ar/docs/reference/behaviors/ui-core) | 266 |\n| [الألعاب](/ar/docs/reference/behaviors/ui-game) | 81 |\n| [التعلّم](/ar/docs/reference/behaviors/ui-learning) | 36 |\n| [AVL](/ar/docs/reference/behaviors/ui-avl) | 4 |\n| [الوكلاء](/ar/docs/reference/behaviors/agent) | 11 |\n| [البنية التحتية](/ar/docs/reference/behaviors/infra) | 31 |\n| [تعلّم الآلة](/ar/docs/reference/behaviors/ml) | 13 |", direction: "rtl", type: "markdown-content" }], direction: "vertical", gap: "md", id: "topics", type: "stack" }], direction: "vertical", gap: "xl", type: "stack" },
  crumbs: [{ href: "/ar/docs", label: "التوثيق" }, { label: "المرجع" }, { label: "السلوكيات" }],
  direction: "rtl",
  lede: "مكتبة @almadar/std العامة: كل سلوك يستطيع البرنامج استيراده بـ `uses`، مرتّبًا حسب الموضوع.",
  navItems: [{ items: [{ active: false, href: "/ar/docs", label: "نظرة عامة" }], label: "نظرة عامة" }, { items: [{ active: false, href: "/ar/docs/getting-started/introduction", label: "المقدمة" }, { active: false, href: "/ar/docs/getting-started/installation", label: "التثبيت" }, { active: false, href: "/ar/docs/getting-started/quickstart", label: "البدء السريع" }, { active: false, href: "/ar/docs/getting-started/project-structure", label: "بنية المشروع" }], label: "البدء" }, { items: [{ active: false, href: "/ar/docs/core-concepts/entities", label: "الكيانات" }, { active: false, href: "/ar/docs/core-concepts/traits", label: "السمات" }, { active: false, href: "/ar/docs/core-concepts/pages", label: "الصفحات" }, { active: false, href: "/ar/docs/core-concepts/closed-circuit", label: "الدائرة المغلقة" }, { active: false, href: "/ar/docs/core-concepts/composition", label: "التركيب" }, { active: false, href: "/ar/docs/core-concepts/patterns", label: "أنماط الواجهة" }, { active: false, href: "/ar/docs/core-concepts/standard-library", label: "المكتبة القياسية" }, { active: false, href: "/ar/docs/core-concepts/localization", label: "التوطين" }], label: "المفاهيم الأساسية" }, { items: [{ active: false, href: "/ar/docs/tutorials/beginner/complete-orbital", label: "مداريّ كامل" }, { active: false, href: "/ar/docs/tutorials/beginner/task-manager", label: "مدير مهام" }, { active: false, href: "/ar/docs/tutorials/intermediate/ui-patterns", label: "أنماط الواجهة" }, { active: false, href: "/ar/docs/tutorials/intermediate/guards", label: "الشروط" }, { active: false, href: "/ar/docs/tutorials/intermediate/cross-orbital", label: "الأحداث بين المداريّات" }, { active: false, href: "/ar/docs/tutorials/advanced/full-app", label: "تطبيق كامل" }, { active: false, href: "/ar/docs/tutorials/advanced/ai-generation", label: "التوليد بالذكاء الاصطناعي" }], label: "دروس تطبيقية" }, { items: [{ items: [{ active: false, href: "/ar/docs/reference/operators", label: "نظرة عامة" }, { active: false, href: "/ar/docs/reference/operators/core", label: "النواة" }, { active: false, href: "/ar/docs/reference/operators/data", label: "البيانات" }, { active: false, href: "/ar/docs/reference/operators/time-format", label: "الوقت والتنسيق" }, { active: false, href: "/ar/docs/reference/operators/validate", label: "التحقق" }, { active: false, href: "/ar/docs/reference/operators/flow", label: "التدفق" }, { active: false, href: "/ar/docs/reference/operators/random", label: "العشوائية" }, { active: false, href: "/ar/docs/reference/operators/spatial", label: "المكان" }, { active: false, href: "/ar/docs/reference/operators/ml", label: "تعلّم الآلة" }, { active: false, href: "/ar/docs/reference/operators/host", label: "المضيف" }], label: "العوامل" }, { items: [{ active: true, href: "/ar/docs/reference/behaviors", label: "نظرة عامة" }, { active: false, href: "/ar/docs/reference/behaviors/ui-core", label: "نواة الواجهة" }, { active: false, href: "/ar/docs/reference/behaviors/ui-game", label: "الألعاب" }, { active: false, href: "/ar/docs/reference/behaviors/ui-learning", label: "التعلّم" }, { active: false, href: "/ar/docs/reference/behaviors/ui-avl", label: "AVL" }, { active: false, href: "/ar/docs/reference/behaviors/agent", label: "الوكلاء" }, { active: false, href: "/ar/docs/reference/behaviors/infra", label: "البنية التحتية" }, { active: false, href: "/ar/docs/reference/behaviors/ml", label: "تعلّم الآلة" }], label: "السلوكيات" }, { active: false, href: "/ar/docs/reference/avl", label: "AVL" }, { active: false, href: "/ar/docs/reference/validation", label: "التحقق" }], label: "المرجع" }, { items: [{ active: false, href: "/ar/docs/downloads/cli", label: "سطر الأوامر" }, { active: false, href: "/ar/docs/downloads/skills", label: "مهارات الوكلاء" }], label: "الأدوات" }, { items: [{ active: false, href: "/ar/docs/community/contributing", label: "المساهمة" }, { active: false, href: "/ar/docs/enterprise/index", label: "للفرق" }], label: "المجتمع" }],
  next: { href: "/ar/docs/reference/behaviors/ui-core", label: "نواة الواجهة" },
  prev: { href: "/ar/docs/reference/operators/host", label: "المضيف" },
  sidebarLabel: "التوثيق",
  title: "السلوكيات القياسية",
  toc: [{ id: "topics", label: "السلوكيات", level: 2 }],
  tocLabel: "في هذه الصفحة",
} as OrbDocReferenceBehaviorsARDocBodyConfig;

export interface OrbDocReferenceBehaviorsARDocBodyLogicReturn {
  state: OrbDocReferenceBehaviorsARDocBodyState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocReferenceBehaviorsARDocBodyEvent | null;
  slotWriter: Partial<Record<OrbDocReferenceBehaviorsARDocBodySlot, OrbDocReferenceBehaviorsARDocBodyEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocReferenceBehaviorsARDocBodyEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocReferenceBehaviorsARDocViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocReferenceBehaviorsARDocBodyEventPayloadMap]: (payload?: OrbDocReferenceBehaviorsARDocBodyEventPayloadMap[K]) => Promise<EventResponse> };
  config: OrbDocReferenceBehaviorsARDocBodyConfig;
}

export function useOrbDocReferenceBehaviorsARDocBodyLogic(config?: Partial<OrbDocReferenceBehaviorsARDocBodyConfig>): OrbDocReferenceBehaviorsARDocBodyLogicReturn {
  const mergedConfig: OrbDocReferenceBehaviorsARDocBodyConfig = { ...DEFAULT_ORB_DOC_REFERENCE_BEHAVIORS_AR_DOC_BODY_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-reference-behaviors-ar-doc-body', orbitalName: 'OrbDocReferenceBehaviorsAR', traitName: 'OrbDocReferenceBehaviorsARDocBody' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocReferenceBehaviorsARDocBodyReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocReferenceBehaviorsARDocView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocReferenceBehaviorsARDocBodySlot[]; clear: OrbDocReferenceBehaviorsARDocBodySlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocReferenceBehaviorsARDocBodySlot[], clear: [] as OrbDocReferenceBehaviorsARDocBodySlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocReferenceBehaviorsARDocView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocReferenceBehaviorsARDocBodyEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocReferenceBehaviorsARDocBody': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocReferenceBehaviorsARDocBodyEventPayloadMap]: (payload?: OrbDocReferenceBehaviorsARDocBodyEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
  };

  useUIEvents(enqueueEvent, 'OrbDocReferenceBehaviorsAR.OrbDocReferenceBehaviorsARDocBody', ['INIT'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocReferenceBehaviorsARDocBody', (): TraitStateSnapshot => ({
      traitName: 'OrbDocReferenceBehaviorsARDocBody',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
